import _ from 'lodash';
import { useLayoutEffect } from 'react';

const RAD2DEG = 57.2957795130823209;

export class Focusable {

  /**
   * This is a CSS selector that is used to find the elements that
   * should be focusable.
   */
  static selector = `.focusable`;
  /**
   * What to do when encountering a `.focusable` element that is
   * missing `tabindex` (and so won't really be focusable).  Can be
   * "set" (which will add `tabindex="-1"`) or "warn" (which will just
   * log a warning).
   */
  static tabindexAction = 'set';
  /**
   * The width of the outline around the focused element.  This should
   * match what is in your CSS, and is used in calculating which
   * elements are "above", "below", etc the focused element.
   */
  static outlineWidth = 3;

  /** @ignore */
  static fallbacks = [];

  constructor( el ) {
    this.el = el;
    if ( ! el.hasAttribute( 'tabindex' ) ) {
      if ( this.constructor.tabindexAction === 'set' ) {
        el.setAttribute( 'tabindex', '-1' );
      } else if ( this.constructor.tabindexAction === 'warn' ) {
        // eslint-disable-next-line no-console
        console.warn( `Focusable elements need tabindex`, el );
      }
    }
  }

  focus() { this.el.focus(); }

  get height() { return this.bounds.height; }
  get width() { return this.bounds.width; }
  get left() { return this.bounds.left + window.scrollX; }
  get right() { return this.bounds.right + window.scrollX; }
  get top() { return this.bounds.top + window.scrollY; }
  get bottom() { return this.bounds.bottom + window.scrollY; }
  get bounds() { return this.el.getBoundingClientRect(); }

  get x() { return this.left + ( this.width / 2 ); }
  get y() { return this.top + ( this.height / 2 ); }


  distanceTo( el ) {
    const a = el.x - this.x;
    const b = el.y - this.y;
    return Math.sqrt( ( a * a ) + ( b * b ) );
  }

  bearingTo( el ) {
    let theta = Math.atan2( el.x - this.x, el.y - this.y );
    if ( theta < 0 ) theta += ( Math.PI * 2 );
    return RAD2DEG * theta;
  }
  directionTo( el ) {
    const deg = this.bearingTo( el );
    const dirs = [ 'N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW' ];
    const idx = Math.round( ( deg % 360 ) / ( 360 / dirs.length ) );
    dirs.push( dirs[ 0 ] ); // N is both 0 and 360
    return dirs[ idx ];
  }

  click() {
    const types = [ 'mousedown', 'click', 'mouseup' ];
    types.forEach( type => {
      const ev = new MouseEvent( type, {
        view        : window,
        bubbles     : true,
        cancelable  : true,
        buttons     : 1,
      } );
      this.el.dispatchEvent( ev );
    } );
  }

  static first() {
    const el = document.querySelector( this.selector );
    if ( el ) return new this( el );
  }

  static active( makeActive=false ) {
    const act = document.activeElement;
    if ( act.matches( this.selector ) ) return new this( act );
    if ( makeActive ) {
      const el = this.first();
      if ( el ) el.focus();
      return el;
    }
    return;
  }

  static getAll() {
    return Array.from( document.querySelectorAll( this.selector ) )
      .map( x => new this( x ) );
  }

  static find( filter, sort ) {
    let res = this.getAll();
    if ( filter ) res = res.filter( filter );
    if ( typeof sort === 'string' ) {
      res = res.sort( ( a, b ) => a[sort] - b[sort] );
    }
    if ( typeof sort === 'function' ) res = res.sort( sort );
    return res;
  }

  next( dir ) {
    const x = this.constructor.outlineWidth;
    const filters = {
      up      : el => el.bottom <= ( this.top + x ),
      down    : el => el.top >= ( this.bottom - x ),
      left    : el => el.right <= ( this.left + x ),
      right   : el => el.left >= ( this.right - x ),
    };
    const filter = filters[ dir ];
    // Find all the elements that are roughly in the specified direction
    // from the specified element, then sort them by how far away they
    // are
    const els = this.constructor.find( filter );
    // return the closest one
    let closestItem = null;
    let closestDistance = Infinity;
    for ( const el of els ) {
      const dist = this.distanceTo( el );
      if ( dist < closestDistance ) {
        closestDistance = dist;
        closestItem = el;
      }
    }

    return closestItem;
  }

  invoke() { this.click(); }

  static annotate( calculation='distance' ) {
    const active = this.active( true );
    const calculations = {
      distance  : x => active.distanceTo( x ),
      bearing   : x => active.bearingTo( x ),
      direction : x => active.directionTo( x ),
    };
    const calc = calculations[ calculation ];
    this.find().forEach( item => {
      item.el.innerHTML = String( calc( item ) );
    } );
  }

  static go( dir ) {
    const active = this.active( true );
    if ( ! active ) return false;
    const next = active.next( dir );
    if ( next ) {
      next.focus();
      return true;
    } else {
      return false;
    }
  }

  static invoke() {
    const active = this.active( false );
    if ( active ) {
      active.invoke();
      return true;
    } else {
      return false;
    }
  }

  // eslint-disable-next-line complexity
  static _handleKeydown( ev ) {
    // In Chrome, when using autofill function to fill in a form, it
    // sometimes raises a `keydown` event that is a regular `Event`
    // rather than a `KeyboardEvent`.  This will break things, so we
    // just ignore those events...
    if ( ! ( ev instanceof KeyboardEvent ) ) return;
    /*
    console.log(
      'SPATIAL', ev,
      this.enabled ? 'enabled' : 'disabled',
      this.activated ? 'activated' : 'deactivated',
    );
    */
    if ( this.disabled ) return;
    const key = this.eventToKey( ev );
    // console.log( 'HANDLING KEY', key );
    // if ( ev.key === 'a' ) return this.annotate(); // for debugging
    if ( key.startsWith( 'Arrow' ) ) {
      const dir = key.replace( /^Arrow/ui, '' ).toLowerCase();
      if ( this.go( dir ) ) {
        ev.preventDefault();
        ev.stopPropagation();
        return;
      }
    }
    if ( key === 'Enter' || key === 'Space' || key === ' ' ) {
      if ( this.invoke() ) {
        ev.preventDefault();
        ev.stopPropagation();
        return;
      }
    }
    for ( const fb of this.fallbacks ) {
      if ( typeof fb !== 'function' ) continue;
      const res = fb( key, ev, this );
      if ( ! res ) continue;
      if ( res === 'abort' ) return;
      if ( res === 'invoke' ) {
        const active = this.active( false );
        if ( active ) active.invoke();
      }
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }
  }
  static handleKeydown = ( ev ) => this._handleKeydown( ev );

  static eventToKey( ev ) {
    const modifiers = [
      'Control',    //
      'Alt',        // Mac: Option
      'Meta',       // Win: N/A, Mac: Command
    ];
    return _.filter( modifiers, m => ev.getModifierState( m ) )
      .concat( ev.key ).join( '-' );
  }

  static activated = false;
  static activate() {
    if ( this.activated ) return;
    window.addEventListener( 'keydown', this.handleKeydown, true );
    this.activated = true;
  }
  static deactivate() {
    if ( ! this.activated ) return;
    window.removeEventListener( 'keydown', this.handleKeydown, true );
    this.activated = false;
  }

  static disabledBy = new Set();

  static get disabled() { return this.disabledBy.size > 0; }
  static get enabled() { return ! this.disabled; }
  static disable( disabler ) { this.disabledBy.add( disabler ); }
  static enable( disabler ) { this.disabledBy.delete( disabler ); }

  static addFallback( func ) { this.fallbacks.unshift( func ); }
  static removeFallback( func ) { _.pull( this.fallbacks, func ); }
}

window.Focusable = Focusable;

/**
 * To enable spatial navigation, have a component call
 * `useSpatialNav( true )` (or just `useSpatialNav()` ).
 *
 * To temporarily disable it while some component is mounted, have it
 * call `useSpatialNav( false )`.
 *
 * The spatial navigation component will be enabled as long as at
 * least one instance of this hook is in use where enable is true, and
 * while no instance of this hook are in use where enable is false.
 *
 * @param {boolean} [enable=true] - Whether to enable or disable.
 * @param {Function} [fallback] - Fallback event handler for
 * key events.
 * @param {object} [config] - Configuration options to apply to
 * {@link Focusable}.
 * @returns {boolean} - Returns true if enabled, false if disabled.
 */
export function useSpatialNav( enable=true, fallback, config ) {
  if ( _.isPlainObject( enable ) ) { config = enable; enable = true; }
  if ( _.isPlainObject( fallback ) ) { config = fallback; fallback = null; }
  if ( _.isFunction( enable ) ) { fallback = enable; enable = true; }
  if ( config ) _.assign( Focusable, config );
  useLayoutEffect( () => {
    if ( enable ) {
      Focusable.activate();
      Focusable.active( true );
      return () => Focusable.deactivate();
    } else {
      const me = Symbol( 'disabler' );
      Focusable.disable( me );
      return () => Focusable.enable( me );
    }
  }, [ enable ] );
  useLayoutEffect( () => {
    Focusable.addFallback( fallback );
    return () => Focusable.removeFallback( fallback );
  }, [ fallback ] );

  return Focusable;
}
